import React from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { Scissors } from 'lucide-react';

const HairLossPatterns = () => {
  const { hairLossPatterns, features } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();

  // Only show on pages with hair loss patterns enabled
  if (!features?.showHairLossPatterns || !hairLossPatterns?.length) {
    return null;
  }

  // Translations for hair loss pattern titles and descriptions
  const patternTranslations = {
    'Norwood 1-2 (Beginnender Haarausfall)': { en: 'Norwood 1-2 (Early Hair Loss)' },
    'Norwood 3 (Geheimratsecken)': { en: 'Norwood 3 (Receding Hairline)' },
    'Norwood 4 (Fortgeschrittener Haarausfall)': { en: 'Norwood 4 (Advanced Hair Loss)' },
    'Norwood 5-6 (Ausgeprägter Haarausfall)': { en: 'Norwood 5-6 (Extensive Hair Loss)' },
    'Norwood 7 (Sehr ausgeprägter Haarausfall)': { en: 'Norwood 7 (Severe Hair Loss)' },
    'Frauen (Lichter Scheitel)': { en: 'Women (Thinning Crown)' },
  };

  const descriptionTranslations = {
    'Leichter Rückgang des Haaransatzes an den Schläfen': {
      en: 'Slight recession of the hairline at the temples'
    },
    'Deutlicher Rückgang des Haaransatzes mit tiefen Geheimratsecken': {
      en: 'Significant recession of the hairline with deep receding corners'
    },
    'Deutlicher Haarausfall im vorderen Bereich und beginnende Tonsur': {
      en: 'Significant hair loss in the front area and beginning crown baldness'
    },
    'Großflächiger Haarausfall mit verbindender Tonsur und Geheimratsecken': {
      en: 'Large-scale hair loss with connecting crown and receding hairline'
    },
    'Nur noch Haarkranz an den Seiten und am Hinterkopf vorhanden': {
      en: 'Only hair crown remaining on the sides and back of the head'
    },
    'Ausdünnung im Scheitelbereich bei Frauen': {
      en: 'Thinning in the crown area in women'
    },
  };

  const getTranslatedTitle = (title) => {
    if (language === 'de') return title;
    return patternTranslations[title]?.en || title;
  };

  const getTranslatedDescription = (desc) => {
    if (language === 'de') return desc;
    return descriptionTranslations[desc]?.en || desc;
  };

  return (
    <section id="haarausfallmuster" className={`py-24 relative overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-[#0a0a0a]' : 'bg-white'
    }`}>
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute top-1/3 left-0 w-[500px] h-[500px] rounded-full blur-[100px] ${
          isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/5'
        }`} />
        <div className={`absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full blur-[80px] ${
          isDark ? 'bg-[#5A8BA6]/5' : 'bg-[#7BA7C2]/5'
        }`} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6 ${
            isDark ? 'bg-[#7BA7C2]/10 border-[#7BA7C2]/20 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 border-[#5A8BA6]/20 text-[#5A8BA6]'
          }`}>
            <Scissors className="w-4 h-4" />
            <span className="text-sm font-medium">
              {language === 'de' ? 'Behandlungsoptionen' : 'Treatment Options'}
            </span>
          </div>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {language === 'de' ? 'Typische ' : 'Typical '}
            <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
              {language === 'de' ? 'Haarausfallmuster' : 'Hair Loss Patterns'}
            </span>
          </h2>
          <p className={`max-w-3xl mx-auto text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {language === 'de' 
              ? 'Jedes Haarausfallmuster erfordert einen individuellen Behandlungsansatz. Wir analysieren Ihr persönliches Muster und entwickeln die optimale Strategie für natürliche Ergebnisse.'
              : 'Each hair loss pattern requires an individual treatment approach. We analyze your personal pattern and develop the optimal strategy for natural results.'}
          </p>
        </div>

        {/* Hair Loss Patterns Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {hairLossPatterns.map((pattern) => (
            <div
              key={pattern.id}
              data-testid={`hairloss-pattern-${pattern.id}`}
              className="group relative h-full"
            >
              {/* Card */}
              <div className={`rounded-2xl overflow-hidden border transition-all duration-500 h-full flex flex-col card-lift ${
                isDark 
                  ? 'bg-[#0f0f0f] border-white/5 hover:border-[#7BA7C2]/30'
                  : 'bg-white border-gray-200 hover:border-[#5A8BA6]/30 shadow-sm hover:shadow-lg'
              }`}>
                {/* Image - clean without text overlay */}
                <div className="relative h-72 overflow-hidden flex-shrink-0">
                  <img
                    src={pattern.image}
                    alt={`${getTranslatedTitle(pattern.title)} Haarausfall Muster - Haartransplantation Dion Hair Clinic`}
                    loading="lazy"
                    className="w-full h-full object-contain object-center group-hover:scale-105 transition-transform duration-500 bg-gray-100"
                  />
                  
                  {/* Norwood Scale Badge */}
                  {pattern.norwood && (
                    <div className={`absolute top-3 right-3 px-3 py-1.5 rounded-lg text-xs font-semibold backdrop-blur-sm ${
                      isDark ? 'bg-[#7BA7C2]/80 text-white' : 'bg-[#5A8BA6]/80 text-white'
                    }`}>
                      {pattern.norwood}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className={`p-5 flex flex-col flex-grow ${
                  isDark ? 'border-t border-white/5' : ''
                }`}>
                  {/* Title in content area */}
                  <h3 className={`text-lg font-bold mb-2 group-hover:text-[#7BA7C2] transition-colors duration-300 ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {getTranslatedTitle(pattern.title)}
                  </h3>
                  
                  <p className={`text-sm leading-relaxed mb-4 flex-grow ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {getTranslatedDescription(pattern.description)}
                  </p>
                  
                  {/* Stats */}
                  <div className={`grid grid-cols-2 gap-3 pt-4 mt-auto border-t ${
                    isDark ? 'border-white/5' : 'border-gray-100'
                  }`}>
                    <div className="text-center">
                      <div className={`text-base font-bold ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
                        {pattern.grafts}
                      </div>
                      <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        {language === 'de' ? 'Typische Grafts' : 'Typical Grafts'}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className={`text-base font-bold ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
                        {language === 'de' ? pattern.sessions : pattern.sessions.replace('Sitzung', 'Session').replace('Sitzungen', 'Sessions')}
                      </div>
                      <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        {language === 'de' ? 'Behandlung' : 'Treatment'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <p className={`text-base mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {language === 'de' 
              ? 'Nicht sicher, welches Muster auf Sie zutrifft?' 
              : 'Not sure which pattern applies to you?'}
          </p>
          <a
            href="#kontakt"
            data-testid="hairloss-cta-button"
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all duration-300 ${
              isDark 
                ? 'bg-[#7BA7C2] text-white hover:bg-[#9BBFD9]' 
                : 'bg-[#5A8BA6] text-white hover:bg-[#4A7B96]'
            }`}
          >
            {language === 'de' ? 'Kostenlose Haaranalyse anfordern' : 'Request Free Hair Analysis'}
          </a>
        </div>
      </div>
    </section>
  );
};

export default HairLossPatterns;
